<template>
  <div class="my-vacancies-container">

    <!-- =========================================================
         HEADER
    ========================================================== -->

    <div class="fixed-header">
      <DefaultHeader
        title="Мои вакансии"
        subtitle="Список ваших вакансий"
        back
        :offset="720"
      />
    </div>


    <!-- =========================================================
         CONTENT
    ========================================================== -->

    <main class="my-vacancies-page">

      <!-- =======================================================
           LOADING
      ======================================================== -->

      <div
        v-if="isLoading"
        class="page-loading"
      >

        <div class="hero-skeleton">

          <div class="skeleton-line skeleton-title"></div>

          <div class="skeleton-line skeleton-description"></div>


          <div class="skeleton-stats">

            <div
              v-for="index in 4"
              :key="index"
              class="skeleton-stat"
            ></div>

          </div>

        </div>


        <div class="vacancies-loading">

          <FavoriteSkeleton
            v-for="index in 4"
            :key="index"
          />

        </div>

      </div>


      <!-- =======================================================
           FULL EMPTY STATE
           НЕ МЕНЯЕМ
      ======================================================== -->

      <section
        v-else-if="hasNoVacancies"
        class="vacancies-empty"
      >

        <div class="empty-background">

          <div class="empty-orbit orbit-one"></div>

          <div class="empty-orbit orbit-two"></div>


          <div class="empty-image-wrapper">

            <img
              src="../assets/my-vacancies-empty.png"
              class="empty-image"
              alt="Вакансий пока нет"
            />


            <div class="empty-status">

              <span class="empty-status-dot"></span>

              Готово к публикации

            </div>

          </div>

        </div>


        <span class="empty-label">
          Начало работы
        </span>


        <h1 class="empty-title">
          Создайте первую вакансию
        </h1>


        <p class="empty-subtext">
          Расскажите о позиции, настройте требования и начните
          получать отклики от подходящих специалистов.
        </p>


        <button
          class="primary-action-button"
          type="button"
          :disabled="isRedirecting"
          @click="goToCreateVacancy"
        >

          <span
            v-if="isRedirecting"
            class="button-loader"
          ></span>


          <template v-else>

            <span class="button-plus">
              +
            </span>

            Разместить вакансию

          </template>

        </button>


        <div class="empty-benefits">

          <span>

            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="m5.5 10.2 2.8 2.8 6.2-6.2"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            Быстрая публикация

          </span>


          <span>

            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="m5.5 10.2 2.8 2.8 6.2-6.2"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            Управление откликами

          </span>


          <span>

            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="m5.5 10.2 2.8 2.8 6.2-6.2"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            Можно редактировать

          </span>

        </div>

      </section>


      <!-- =======================================================
           DASHBOARD
      ======================================================== -->

      <template v-else>

        <!-- =====================================================
             HERO
        ====================================================== -->

        <section class="vacancies-hero">

          <div class="hero-glow hero-glow-left"></div>
          <div class="hero-glow hero-glow-right"></div>


          <div class="hero-top">

            <div class="hero-main">

              <div class="hero-icon">

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >

                  <path
                    d="M8 7V5.8A2.8 2.8 0 0 1 10.8 3h2.4A2.8 2.8 0 0 1 16 5.8V7"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                  />

                  <rect
                    x="3"
                    y="7"
                    width="18"
                    height="13"
                    rx="3"
                    stroke="currentColor"
                    stroke-width="1.8"
                  />

                  <path
                    d="M3.5 12.5c5.5 2 11.5 2 17 0M10 13.8h4"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                  />

                </svg>

              </div>


              <div class="hero-copy">

                <span class="hero-eyebrow">
                  Центр управления
                </span>

                <h1>
                  Ваши вакансии
                </h1>

                <p>
                  Следите за публикациями, проверкой и архивом
                  в одном месте.
                </p>

              </div>

            </div>


            <button
              class="create-vacancy-button"
              type="button"
              :disabled="isRedirecting"
              :class="{ loading: isRedirecting }"
              @click="goToCreateVacancy"
            >

              <span
                v-if="isRedirecting"
                class="button-loader"
              ></span>


              <template v-else>

                <span class="create-button-icon">
                  +
                </span>

                Создать вакансию

              </template>

            </button>

          </div>


          <!-- STATS -->

          <div class="hero-statistics">

            <!-- TOTAL -->

            <article class="stat-card">

              <div class="stat-icon stat-icon-total">

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <rect
                    x="4"
                    y="4"
                    width="16"
                    height="16"
                    rx="4"
                    stroke="currentColor"
                    stroke-width="1.8"
                  />

                  <path
                    d="M8 9h8M8 13h8M8 17h5"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                  />
                </svg>

              </div>


              <div class="stat-content">

                <span class="stat-label">
                  Всего
                </span>

                <strong>
                  {{ totalVacanciesCount }}
                </strong>

              </div>

            </article>


            <!-- ACTIVE -->

            <article class="stat-card">

              <div class="stat-icon stat-icon-active">

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="8.5"
                    stroke="currentColor"
                    stroke-width="1.8"
                  />

                  <path
                    d="m8 12 2.6 2.6L16.5 9"
                    stroke="currentColor"
                    stroke-width="1.9"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

              </div>


              <div class="stat-content">

                <span class="stat-label">
                  Активные
                </span>

                <strong>
                  {{ activeVacancies.length }}
                </strong>

              </div>

            </article>


            <!-- PENDING -->

            <article class="stat-card">

              <div class="stat-icon stat-icon-pending">

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="8.5"
                    stroke="currentColor"
                    stroke-width="1.8"
                  />

                  <path
                    d="M12 7.5v5l3 1.8"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

              </div>


              <div class="stat-content">

                <span class="stat-label">
                  На проверке
                </span>

                <strong>
                  {{ pendingVacancies.length }}
                </strong>

              </div>

            </article>


            <!-- FROZEN -->

            <article class="stat-card">

              <div class="stat-icon stat-icon-archive">

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <rect
                    x="4"
                    y="7"
                    width="16"
                    height="13"
                    rx="3"
                    stroke="currentColor"
                    stroke-width="1.8"
                  />

                  <path
                    d="M3 7h18V4H3v3ZM9 12h6"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

              </div>


              <div class="stat-content">

                <span class="stat-label">
                  В архиве
                </span>

                <strong>
                  {{ frozenVacancies.length }}
                </strong>

              </div>

            </article>

          </div>

        </section>


        <!-- =====================================================
             ACTIVE
        ====================================================== -->

        <section
          class="vacancies-section active-section"
          :class="{
            'drag-over': isDraggingOverActive
          }"
          @dragover.prevent="isDraggingOverActive = true"
          @dragleave="isDraggingOverActive = false"
          @drop="handleDropToActive"
        >

          <header class="section-header">

            <div class="section-title-wrapper">

              <div class="section-icon active-section-icon">

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="8.5"
                    stroke="currentColor"
                    stroke-width="1.8"
                  />

                  <path
                    d="m8 12 2.6 2.6L16.5 9"
                    stroke="currentColor"
                    stroke-width="1.9"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

              </div>


              <div>

                <div class="section-title-line">

                  <h2>
                    Активные вакансии
                  </h2>

                  <span class="section-counter">
                    {{ activeVacancies.length }}
                  </span>

                </div>


                <p>
                  Опубликованы и доступны кандидатам прямо сейчас.
                </p>

              </div>

            </div>

          </header>


          <Transition name="drop-message">

            <div
              v-if="isDraggingOverActive"
              class="drop-zone-message"
            >

              <div class="drop-zone-icon">

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M12 4v12M7.5 8.5 12 4l4.5 4.5"
                    stroke="currentColor"
                    stroke-width="1.9"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />

                  <path
                    d="M5 15v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                  />
                </svg>

              </div>


              <div>

                <strong>
                  Вернуть вакансию
                </strong>

                <span>
                  Отпустите карточку, чтобы снова опубликовать её
                </span>

              </div>

            </div>

          </Transition>


          <div
            v-if="activeVacancies.length === 0"
            class="section-empty"
          >

            <div class="section-empty-icon">

              <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M8 7V5.8A2.8 2.8 0 0 1 10.8 3h2.4A2.8 2.8 0 0 1 16 5.8V7"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                />

                <rect
                  x="3"
                  y="7"
                  width="18"
                  height="13"
                  rx="3"
                  stroke="currentColor"
                  stroke-width="1.7"
                />

                <path
                  d="M9 13h6"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                />
              </svg>

            </div>


            <div class="section-empty-copy">

              <strong>
                Нет активных вакансий
              </strong>

              <span>
                Здесь появятся вакансии после публикации.
              </span>

            </div>


            <button
              type="button"
              class="section-empty-button"
              :disabled="isRedirecting"
              @click="goToCreateVacancy"
            >
              Создать вакансию
            </button>

          </div>


          <TransitionGroup
            v-else
            name="vacancy-list"
            tag="div"
            class="vacancies-list"
          >

            <div
              v-for="vacancy in activeVacancies"
              :key="getVacancyID(vacancy)"
              class="vacancy-card-wrapper"
            >

              <div class="vacancy-card-status">

                <span class="status-dot"></span>

                Активна

              </div>


              <MyVacancyCard
                :vacancy="vacancy"
                @click="openVacancy(vacancy)"
                @edit="editVacancy"
                @delete="requestDeleteVacancy"
              />

            </div>

          </TransitionGroup>

        </section>


        <!-- =====================================================
             PENDING
        ====================================================== -->

        <section class="vacancies-section pending-section">

          <header class="section-header">

            <div class="section-title-wrapper">

              <div class="section-icon pending-section-icon">

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="8.5"
                    stroke="currentColor"
                    stroke-width="1.8"
                  />

                  <path
                    d="M12 7.5v5l3 1.8"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

              </div>


              <div>

                <div class="section-title-line">

                  <h2>
                    На проверке
                  </h2>

                  <span class="section-counter pending-counter">
                    {{ pendingVacancies.length }}
                  </span>

                </div>


                <p>
                  Вакансии проходят проверку перед публикацией.
                </p>

              </div>

            </div>


            <div
              v-if="pendingVacancies.length"
              class="pending-info"
            >

              <span class="pending-info-dot"></span>

              Проверяются

            </div>

          </header>


          <div
            v-if="pendingVacancies.length === 0"
            class="pending-empty"
          >

            <div class="pending-empty-icon">

              <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="8"
                  stroke="currentColor"
                  stroke-width="1.6"
                />

                <path
                  d="M12 8v4M12 15.5v.2"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
              </svg>

            </div>


            <div>

              <strong>
                Нет вакансий на проверке
              </strong>

              <span>
                Все отправленные вакансии уже обработаны.
              </span>

            </div>

          </div>


          <TransitionGroup
            v-else
            name="vacancy-list"
            tag="div"
            class="vacancies-list"
          >

            <div
              v-for="vacancy in pendingVacancies"
              :key="getVacancyID(vacancy)"
              class="vacancy-card-wrapper pending-card-wrapper"
            >

              <div class="vacancy-card-status pending-status">

                <span class="pending-status-icon">

                  <svg
                    viewBox="0 0 18 18"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      cx="9"
                      cy="9"
                      r="6.4"
                      stroke="currentColor"
                      stroke-width="1.4"
                    />

                    <path
                      d="M9 5.8v3.7l2.3 1.4"
                      stroke="currentColor"
                      stroke-width="1.4"
                      stroke-linecap="round"
                    />
                  </svg>

                </span>

                На проверке

              </div>


              <MyVacancyCard
                :vacancy="vacancy"
                @click="openVacancy(vacancy)"
                @edit="editVacancy"
                @delete="requestDeleteVacancy"
              />

            </div>

          </TransitionGroup>

        </section>


        <!-- =====================================================
             ARCHIVE
        ====================================================== -->

        <section
          class="vacancies-section archive-section"
          :class="{
            expanded: isArchiveExpanded,
            'drag-over': isDraggingOverArchive
          }"
          @dragover.prevent="isDraggingOverArchive = true"
          @dragleave="isDraggingOverArchive = false"
          @drop="handleDropToArchive"
        >

          <button
            type="button"
            class="archive-header"
            :aria-expanded="isArchiveExpanded"
            @click="toggleArchive"
          >

            <div class="section-title-wrapper">

              <div class="section-icon archive-section-icon">

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <rect
                    x="4"
                    y="7"
                    width="16"
                    height="13"
                    rx="3"
                    stroke="currentColor"
                    stroke-width="1.8"
                  />

                  <path
                    d="M3 7h18V4H3v3ZM9 12h6"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

              </div>


              <div>

                <div class="section-title-line">

                  <h2>
                    Архив
                  </h2>

                  <span class="section-counter archive-counter">
                    {{ frozenVacancies.length }}
                  </span>

                </div>


                <p>
                  Скрытые вакансии, которые можно вернуть в публикацию.
                </p>

              </div>

            </div>


            <span class="archive-toggle">

              {{
                isArchiveExpanded
                  ? "Свернуть"
                  : "Показать"
              }}

              <svg
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="m6.5 8 3.5 3.5L13.5 8"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

            </span>

          </button>


          <Transition name="archive-content">

            <div
              v-if="isArchiveExpanded"
              class="archive-content"
            >

              <Transition name="drop-message">

                <div
                  v-if="isDraggingOverArchive"
                  class="drop-zone-message archive-drop-message"
                >

                  <div class="drop-zone-icon">

                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M12 4v12M7.5 11.5 12 16l4.5-4.5"
                        stroke="currentColor"
                        stroke-width="1.9"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />

                      <path
                        d="M5 15v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3"
                        stroke="currentColor"
                        stroke-width="1.8"
                        stroke-linecap="round"
                      />
                    </svg>

                  </div>


                  <div>

                    <strong>
                      Отправить в архив
                    </strong>

                    <span>
                      Вакансия исчезнет из общего каталога
                    </span>

                  </div>

                </div>

              </Transition>


              <div
                v-if="frozenVacancies.length === 0"
                class="archive-empty"
              >

                <img
                  src="../assets/archive-vacancies.png"
                  class="archive-empty-image"
                  alt="Архив вакансий"
                />


                <div class="archive-empty-copy">

                  <strong>
                    Архив пока пуст
                  </strong>

                  <span>
                    Перетащите сюда активную вакансию,
                    чтобы временно скрыть её без удаления.
                  </span>

                </div>

              </div>


              <TransitionGroup
                v-else
                name="vacancy-list"
                tag="div"
                class="vacancies-list"
              >

                <div
                  v-for="vacancy in frozenVacancies"
                  :key="getVacancyID(vacancy)"
                  class="vacancy-card-wrapper frozen-card-wrapper"
                >

                  <div class="vacancy-card-status archive-status">

                    <span class="archive-status-icon">

                      <svg
                        viewBox="0 0 20 20"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M4 7h12M6 7v8h8V7M7 4h6l1 3H6l1-3Z"
                          stroke="currentColor"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>

                    </span>

                    В архиве

                  </div>


                  <MyVacancyCard
                    :vacancy="vacancy"
                    @click="openVacancy(vacancy)"
                    @edit="editVacancy"
                    @delete="requestDeleteVacancy"
                  />

                </div>

              </TransitionGroup>

            </div>

          </Transition>

        </section>

      </template>

    </main>


    <!-- =========================================================
         RESPONSE DRAWER
    ========================================================== -->

    <VacancyResponseDrawer
      v-if="isResponseDrawerOpen"
      :vacancy="selectedVacancy"
      @close="closeResponseDrawer"
    />


    <!-- =========================================================
         FOOTER
    ========================================================== -->

    <div class="fixed-footer">
      <Footer/>
    </div>

  </div>


  <!-- =========================================================
       COMPANY DRAWER
  ========================================================== -->

  <ProfileCompanyDrawer
    :visible="isCompanyDrawerOpen"
    @close="isCompanyDrawerOpen = false"
    @startOverlay="globalOverlay = true"
    @stopOverlay="globalOverlay = false"
    @company-added="onCompanyAdded"
  />


  <!-- =========================================================
       GLOBAL OVERLAY
  ========================================================== -->

  <Transition name="overlay">

    <div
      v-if="globalOverlay"
      class="global-overlay"
    >

      <div class="global-loader-card">

        <div class="global-spinner"></div>

        <span>
          Обновляем данные...
        </span>

      </div>

    </div>

  </Transition>


  <!-- =========================================================
       SNACKBAR
  ========================================================== -->

  <Snackbar
    :message="snackbarMessage"
    :type="snackbarType"
    :show="snackbarShow"
  />


  <!-- =========================================================
       PAYWALL
  ========================================================== -->

  <teleport to="body">

    <Paywall
      v-if="showPaywall"
      @close="showPaywall = false"
    />

  </teleport>


  <teleport to="body">

<Transition name="delete-modal">

  <div
    v-if="isDeleteModalOpen"
    class="delete-modal-overlay"
    @click.self="closeDeleteModal"
  >

    <div class="delete-modal">

      <div class="delete-modal-icon">

        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M4.5 7H19.5"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />

          <path
            d="M9 4H15"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />

          <path
            d="M7 7L7.7 18C7.76 19.1 8.68 20 9.8 20H14.2C15.32 20 16.24 19.1 16.3 18L17 7"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />

          <path
            d="M10 10V16M14 10V16"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          />
        </svg>

      </div>


      <div class="delete-modal-copy">

        <span class="delete-modal-label">
          Удаление вакансии
        </span>

        <h3>
          Вы точно хотите удалить?
        </h3>

        <p>
          Вакансия
          <strong>
            {{ vacancyToDelete?.title || "Без названия" }}
          </strong>
          будет удалена без возможности восстановления.
        </p>

      </div>


      <div class="delete-modal-actions">

        <button
          type="button"
          class="delete-modal-cancel"
          :disabled="isDeletingVacancy"
          @click="closeDeleteModal"
        >
          Отмена
        </button>


        <button
          type="button"
          class="delete-modal-confirm"
          :disabled="isDeletingVacancy"
          @click="confirmDeleteVacancy"
        >

          <span
            v-if="isDeletingVacancy"
            class="delete-button-spinner"
          ></span>

          <template v-else>
            Удалить
          </template>

        </button>

      </div>

    </div>

  </div>

</Transition>

</teleport>

</template>


<script setup>

import {
  computed,
  onMounted,
  ref
} from "vue";

import {
  useRouter
} from "vue-router";

import Paywall
  from "../views/Paywall.vue";

import VacancyResponseDrawer
  from "../views/VacancyResponsesDrawer.vue";

import DefaultHeader
  from "../components/MyVacanciesHeader.vue";

import MyVacancyCard
  from "../components/MyVacancyCard.vue";

import Footer
  from "../components/MiniFooter.vue";

import FavoriteSkeleton
  from "../components/FavoriteSkeleton.vue";

import ProfileCompanyDrawer
  from "../components/profile/ProfileCompanyDrawer.vue";

import Snackbar
  from "../components/Snackbar.vue";

import MyVacanciesService
  from "../api/MyVacanciesService";

import PremiumService
  from "../api/PremiumService";


/* ============================================================
   ROUTER
============================================================ */

const router =
  useRouter();


/* ============================================================
   VACANCIES
============================================================ */

const activeVacancies =
  ref([]);

const pendingVacancies =
  ref([]);

const frozenVacancies =
  ref([]);


/* ============================================================
   UI STATE
============================================================ */

const isLoading =
  ref(true);

const isRedirecting =
  ref(false);

const selectedVacancy =
  ref(null);

const isResponseDrawerOpen =
  ref(false);

const isArchiveExpanded =
  ref(false);


/* ============================================================
   DRAWERS
============================================================ */

const isCompanyDrawerOpen =
  ref(false);

const showPaywall =
  ref(false);


/* ============================================================
   DRAG & DROP
============================================================ */

const isDraggingOverActive =
  ref(false);

const isDraggingOverArchive =
  ref(false);


/* ============================================================
   OVERLAY
============================================================ */

const globalOverlay =
  ref(false);


/* ============================================================
   SNACKBAR
============================================================ */

const snackbarShow =
  ref(false);

const snackbarMessage =
  ref("");

const snackbarType =
  ref("success");


/* ============================================================
   COMPUTED
============================================================ */

const totalVacanciesCount =
  computed(() => {

    return (
      activeVacancies.value.length +
      pendingVacancies.value.length +
      frozenVacancies.value.length
    );

  });

const vacancyToDelete = ref(null);
const isDeleteModalOpen = ref(false);
const isDeletingVacancy = ref(false);


const hasNoVacancies =
  computed(() => {

    return (
      activeVacancies.value.length === 0 &&
      pendingVacancies.value.length === 0 &&
      frozenVacancies.value.length === 0
    );

  });


/* ============================================================
   SNACKBAR
============================================================ */

function showSnackbar(
  message,
  type = "success"
) {

  snackbarMessage.value =
    message;

  snackbarType.value =
    type;

  snackbarShow.value =
    false;


  window.setTimeout(
    () => {

      snackbarShow.value =
        true;

    },
    10
  );

}


/* ============================================================
   LOAD VACANCIES
============================================================ */

async function loadVacancies() {

  isLoading.value =
    true;


  try {

    const response =
      await MyVacanciesService
        .getMyVacancies();


    activeVacancies.value =
      Array.isArray(
        response?.active_vacancies
      )
        ? response.active_vacancies
        : [];


    pendingVacancies.value =
      Array.isArray(
        response?.pending_vacancies
      )
        ? response.pending_vacancies
        : [];


    frozenVacancies.value =
      Array.isArray(
        response?.frozen_vacancies
      )
        ? response.frozen_vacancies
        : [];


    isArchiveExpanded.value =
      false;


    console.log(
      "✅ Активные вакансии:",
      activeVacancies.value
    );


    console.log(
      "🕓 Вакансии на проверке:",
      pendingVacancies.value
    );


    console.log(
      "📦 Архивные вакансии:",
      frozenVacancies.value
    );

  } catch (error) {

    console.error(
      "❌ Ошибка загрузки вакансий:",
      error
    );


    showSnackbar(
      "Не удалось загрузить вакансии",
      "error"
    );

  } finally {

    isLoading.value =
      false;

  }

}


/* ============================================================
   LIFECYCLE
============================================================ */

onMounted(
  () => {

    loadVacancies();

  }
);


/* ============================================================
   VACANCY
============================================================ */

function openVacancy(
  vacancy
) {

  selectedVacancy.value =
    vacancy;

  isResponseDrawerOpen.value =
    true;

}


function closeResponseDrawer() {

  isResponseDrawerOpen.value =
    false;

  selectedVacancy.value =
    null;

}


function getVacancyID(
  vacancy
) {

  return String(

    vacancy?.id ||
    vacancy?._id ||
    vacancy?.vacancy_id ||
    ""

  );

}


function editVacancy(
  vacancy
) {

  const vacancyId =
    getVacancyID(
      vacancy
    );


  if (!vacancyId) {

    console.error(
      "❌ У вакансии отсутствует ID:",
      vacancy
    );

    return;

  }


  localStorage.setItem(
    "vacancy_to_edit",
    JSON.stringify(vacancy)
  );


  router.push(
    `/edit-my-vacancy/${vacancyId}`
  );

}


/* ============================================================
   ARCHIVE
============================================================ */

function toggleArchive() {

  isArchiveExpanded.value =
    !isArchiveExpanded.value;

}


/* ============================================================
   COMPANY
============================================================ */

async function onCompanyAdded() {

  isCompanyDrawerOpen.value =
    false;

  globalOverlay.value =
    false;


  await goToCreateVacancy();

}


/* ============================================================
   CREATE VACANCY
============================================================ */

async function goToCreateVacancy() {

  if (
    isRedirecting.value
  ) {
    return;
  }


  isRedirecting.value =
    true;

  showPaywall.value =
    false;

  isCompanyDrawerOpen.value =
    false;


  try {

    console.log(
      "🚀 Проверяем возможность создания вакансии"
    );


    const premium =
      await PremiumService
        .fetchMySubscription();


    if (!premium) {

      throw new Error(
        "Сервер не вернул настройки подписки"
      );

    }


    console.log(
      "📦 Ограничения создания вакансии:",
      premium.toJSON()
    );


    if (
      premium.hasNoCompanies
    ) {

      isCompanyDrawerOpen.value =
        true;

      return;

    }


    if (
      premium.shouldShowVacancyPaywall
    ) {

      showPaywall.value =
        true;

      return;

    }


    if (
      premium.hasMultipleCompanies
    ) {

      await router.push({

        path:
          "/create-vacancy",

        query: {
          "is-multiple-companies":
            "true"
        }

      });


      return;

    }


    await router.push(
      "/create-vacancy"
    );

  } catch (error) {

    console.error(
      "❌ Ошибка проверки возможности создания вакансии:",
      error?.response?.data ||
      error?.message ||
      error
    );


    showSnackbar(
      "Не удалось проверить подписку. Попробуйте ещё раз",
      "error"
    );

  } finally {

    isRedirecting.value =
      false;

  }

}


/* ============================================================
   DROP HELPERS
============================================================ */

function getVacancyIDFromDrop(
  event
) {

  return event.dataTransfer
    ?.getData(
      "vacancy_id"
    );

}


/* ============================================================
   MOVE ACTIVE -> FROZEN
============================================================ */

async function handleDropToArchive(
  event
) {

  isDraggingOverArchive.value =
    false;


  const vacancyId =
    getVacancyIDFromDrop(
      event
    );


  if (!vacancyId) {
    return;
  }


  const movedVacancy =
    activeVacancies.value
      .find(
        vacancy =>
          getVacancyID(vacancy) ===
          String(vacancyId)
      );


  /*
   * Pending вакансии сюда специально не попадают.
   * Архивируем только active_vacancies.
   */
  if (!movedVacancy) {
    return;
  }


  try {

    await MyVacanciesService
      .freezeVacancy(
        vacancyId
      );


    activeVacancies.value =
      activeVacancies.value
        .filter(
          vacancy =>
            getVacancyID(vacancy) !==
            String(vacancyId)
        );


    frozenVacancies.value.unshift(
      movedVacancy
    );


    isArchiveExpanded.value =
      true;


    showSnackbar(
      "Вакансия перемещена в архив"
    );

  } catch (error) {

    console.error(
      "❌ Ошибка перемещения в архив:",
      error
    );


    showSnackbar(
      "Не удалось переместить вакансию",
      "error"
    );

  }

}


/* ============================================================
   RESTORE FROZEN -> ACTIVE
============================================================ */

async function handleDropToActive(
  event
) {

  isDraggingOverActive.value =
    false;


  const vacancyId =
    getVacancyIDFromDrop(
      event
    );


  if (!vacancyId) {
    return;
  }


  const movedVacancy =
    frozenVacancies.value
      .find(
        vacancy =>
          getVacancyID(vacancy) ===
          String(vacancyId)
      );


  if (!movedVacancy) {
    return;
  }


  try {

    await MyVacanciesService
      .unfreezeVacancy(
        vacancyId
      );


    frozenVacancies.value =
      frozenVacancies.value
        .filter(
          vacancy =>
            getVacancyID(vacancy) !==
            String(vacancyId)
        );


    activeVacancies.value.unshift(
      movedVacancy
    );


    showSnackbar(
      "Вакансия восстановлена"
    );

  } catch (error) {

    console.error(
      "❌ Ошибка восстановления вакансии:",
      error
    );


    showSnackbar(
      "Не удалось восстановить вакансию",
      "error"
    );

  }

}

function requestDeleteVacancy(vacancy) {

if (!vacancy) {
  return;
}

vacancyToDelete.value =
  vacancy;

isDeleteModalOpen.value =
  true;

}

function closeDeleteModal() {

if (isDeletingVacancy.value) {
  return;
}

isDeleteModalOpen.value =
  false;

vacancyToDelete.value =
  null;

}

async function confirmDeleteVacancy() {

const vacancy =
  vacancyToDelete.value;


if (
  !vacancy ||
  isDeletingVacancy.value
) {
  return;
}


const vacancyId =
  getVacancyID(vacancy);


if (!vacancyId) {

  showSnackbar(
    "Не удалось определить вакансию",
    "error"
  );

  closeDeleteModal();

  return;
}


isDeletingVacancy.value =
  true;


try {

  await MyVacanciesService
    .deleteVacancy(
      vacancyId
    );


  /*
   * Сначала закрываем модалку.
   */

  isDeleteModalOpen.value =
    false;


  /*
   * А теперь удаляем элемент из массива.
   * TransitionGroup сам проиграет leave-анимацию.
   */

  activeVacancies.value =
    activeVacancies.value.filter(
      item =>
        getVacancyID(item) !== vacancyId
    );


  pendingVacancies.value =
    pendingVacancies.value.filter(
      item =>
        getVacancyID(item) !== vacancyId
    );


  frozenVacancies.value =
    frozenVacancies.value.filter(
      item =>
        getVacancyID(item) !== vacancyId
    );


  if (
    selectedVacancy.value &&
    getVacancyID(
      selectedVacancy.value
    ) === vacancyId
  ) {

    closeResponseDrawer();

  }


  vacancyToDelete.value =
    null;


  showSnackbar(
    "Вакансия удалена"
  );

} catch (error) {

  console.error(
    "❌ Ошибка удаления вакансии:",
    error?.response?.data ||
    error?.message ||
    error
  );


  showSnackbar(
    "Не удалось удалить вакансию",
    "error"
  );

} finally {

  isDeletingVacancy.value =
    false;

}

}

async function deleteVacancy(vacancy) {

const vacancyId =
  getVacancyID(vacancy);


if (!vacancyId) {

  console.error(
    "❌ Не удалось определить ID вакансии:",
    vacancy
  );

  showSnackbar(
    "Не удалось определить вакансию",
    "error"
  );

  return;
}


console.log(
  "🗑 Удаляем вакансию:",
  vacancyId
);


try {

  await MyVacanciesService
    .deleteVacancy(
      vacancyId
    );


  /*
   * Вакансия может находиться
   * в любом из трёх массивов.
   */

  activeVacancies.value =
    activeVacancies.value.filter(
      item =>
        getVacancyID(item) !==
        vacancyId
    );


  pendingVacancies.value =
    pendingVacancies.value.filter(
      item =>
        getVacancyID(item) !==
        vacancyId
    );


  frozenVacancies.value =
    frozenVacancies.value.filter(
      item =>
        getVacancyID(item) !==
        vacancyId
    );

  if (
    selectedVacancy.value &&
    getVacancyID(
      selectedVacancy.value
    ) === vacancyId
  ) {

    closeResponseDrawer();

  }


  console.log(
    "✅ Вакансия удалена:",
    vacancyId
  );


  showSnackbar(
    "Вакансия удалена"
  );

} catch (error) {

  console.error(
    "❌ Ошибка удаления вакансии:",
    error?.response?.data ||
    error?.message ||
    error
  );


  showSnackbar(
    "Не удалось удалить вакансию",
    "error"
  );

}

}

</script>


<style scoped>

/* ============================================================
   BASE
============================================================ */

* {
  box-sizing: border-box;
}


.my-vacancies-container {
  width: 100%;
  height: 100dvh;

  display: flex;
  flex-direction: column;

  overflow: hidden;

  color: var(--text-primary);
  background: var(--background-color);
}


.my-vacancies-page {
  width:
    min(
      calc(100% - 40px),
      1240px
    );

  flex: 1;

  overflow-y: auto;
  overflow-x: hidden;

  margin: 0 auto;

  padding:
    34px
    0
    90px;

  scrollbar-width: none;
}


.my-vacancies-page::-webkit-scrollbar {
  display: none;
}


/* ============================================================
   FIXED AREAS
============================================================ */

.fixed-header {
  height: 80px;

  flex-shrink: 0;

  z-index: 20;
}


.fixed-footer {
  height: 70px;

  flex-shrink: 0;

  z-index: 20;
}


/* ============================================================
   LOADING
============================================================ */

.page-loading {
  display: flex;
  flex-direction: column;

  gap: 26px;
}


.hero-skeleton {
  min-height: 270px;

  padding: 34px;

  overflow: hidden;

  background: #ffffff;

  border:
    1px solid
    rgba(30, 50, 80, .07);

  border-radius: 28px;
}


.skeleton-line,
.skeleton-stat {
  position: relative;

  overflow: hidden;

  background:
    #eef0f4;
}


.skeleton-line::after,
.skeleton-stat::after {
  content: "";

  position: absolute;

  inset: 0;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, .7),
      transparent
    );

  transform:
    translateX(-100%);

  animation:
    skeleton-shimmer
    1.4s
    infinite;
}


.skeleton-title {
  width: 290px;
  height: 30px;

  border-radius: 10px;
}


.skeleton-description {
  width:
    min(
      100%,
      520px
    );

  height: 16px;

  margin-top: 15px;

  border-radius: 7px;
}


.skeleton-stats {
  display: grid;

  grid-template-columns:
    repeat(
      4,
      minmax(0, 1fr)
    );

  gap: 12px;

  margin-top: 70px;
}


.skeleton-stat {
  height: 82px;

  border-radius: 18px;
}


.vacancies-loading {
  display: grid;

  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    );

  gap: 20px;
}


/* ============================================================
   HERO
============================================================ */

.vacancies-hero {
  position: relative;

  overflow: hidden;

  padding:
    32px
    34px
    28px;

  background:
    linear-gradient(
      135deg,
      rgba(52, 127, 231, .075),
      rgba(105, 91, 226, .035)
    ),
    #ffffff;

  border:
    1px solid
    rgba(52, 110, 205, .095);

  border-radius: 28px;

  box-shadow:
    0
    18px
    48px
    rgba(26, 52, 90, .055);

  animation:
    content-appear
    .4s
    ease
    both;
}


.hero-glow {
  position: absolute;

  pointer-events: none;

  border-radius: 50%;

  filter:
    blur(24px);
}


.hero-glow-left {
  top: -190px;
  left: -100px;

  width: 390px;
  height: 390px;

  background:
    rgba(52, 127, 231, .12);
}


.hero-glow-right {
  right: -100px;
  bottom: -230px;

  width: 430px;
  height: 430px;

  background:
    rgba(108, 87, 229, .09);
}


.hero-top {
  position: relative;

  z-index: 2;

  display: flex;

  align-items: flex-start;

  justify-content: space-between;

  gap: 28px;
}


.hero-main {
  min-width: 0;

  display: flex;

  align-items: flex-start;

  gap: 16px;
}


.hero-icon {
  width: 54px;
  height: 54px;

  flex:
    0
    0
    54px;

  display: flex;

  align-items: center;

  justify-content: center;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #3998f3,
      #4176e8 60%,
      #6559dc
    );

  border-radius: 17px;

  box-shadow:
    0
    12px
    24px
    rgba(53, 117, 231, .22),
    inset
    0
    1px
    0
    rgba(255, 255, 255, .22);
}


.hero-icon svg {
  width: 28px;
  height: 28px;
}


.hero-copy {
  min-width: 0;
}


.hero-eyebrow {
  display: block;

  margin-bottom: 5px;

  color:
    #738197;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  letter-spacing: .08em;

  text-transform: uppercase;
}


.hero-copy h1 {
  margin: 0;

  color:
    #262b34;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 26px;

  line-height: 1.2;

  letter-spacing: -.55px;
}


.hero-copy p {
  max-width: 530px;

  margin:
    8px
    0
    0;

  color:
    #7b828e;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 12px;

  line-height: 1.55;
}


/* ============================================================
   CREATE BUTTON
============================================================ */

.create-vacancy-button,
.primary-action-button {
  position: relative;

  z-index: 3;

  min-height: 48px;

  display: inline-flex;

  align-items: center;

  justify-content: center;

  gap: 8px;

  padding:
    0
    20px;

  color: #ffffff;

  background:
    linear-gradient(
      110deg,
      #2887f7,
      #4d72eb,
      #35a8f5,
      #2887f7
    );

  background-size:
    230%
    100%;

  border: none;

  border-radius: 15px;

  box-shadow:
    0
    10px
    23px
    rgba(40, 120, 235, .23);

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 12px;

  cursor: pointer;

  animation:
    action-gradient
    4s
    linear
    infinite;

  transition:
    transform .18s ease,
    box-shadow .18s ease,
    opacity .18s ease;
}


.create-vacancy-button:hover:not(:disabled),
.primary-action-button:hover:not(:disabled) {
  transform:
    translateY(-2px);

  box-shadow:
    0
    14px
    28px
    rgba(40, 120, 235, .30);
}


.create-vacancy-button:disabled,
.primary-action-button:disabled {
  cursor:
    not-allowed;

  opacity:
    .55;
}


.create-button-icon,
.button-plus {
  width: 24px;
  height: 24px;

  display: flex;

  align-items: center;

  justify-content: center;

  color: #ffffff;

  background:
    rgba(255, 255, 255, .16);

  border:
    1px solid
    rgba(255, 255, 255, .16);

  border-radius: 8px;

  font-size: 19px;

  line-height: 1;
}


.button-loader {
  width: 18px;
  height: 18px;

  border:
    2px solid
    rgba(255, 255, 255, .35);

  border-top-color:
    #ffffff;

  border-radius: 50%;

  animation:
    spin
    .75s
    linear
    infinite;
}


/* ============================================================
   STATISTICS
============================================================ */

.hero-statistics {
  position: relative;

  z-index: 2;

  display: grid;

  grid-template-columns:
    repeat(
      4,
      minmax(0, 1fr)
    );

  gap: 11px;

  margin-top: 28px;
}


.stat-card {
  min-width: 0;

  display: flex;

  align-items: center;

  gap: 11px;

  padding:
    13px
    14px;

  background:
    rgba(255, 255, 255, .72);

  border:
    1px solid
    rgba(30, 70, 130, .07);

  border-radius: 16px;

  backdrop-filter:
    blur(14px);

  -webkit-backdrop-filter:
    blur(14px);

  box-shadow:
    0
    7px
    18px
    rgba(35, 63, 110, .04);
}


.stat-icon {
  width: 38px;
  height: 38px;

  flex:
    0
    0
    38px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 12px;
}


.stat-icon svg {
  width: 20px;
  height: 20px;
}


.stat-icon-total {
  color: #497fe7;

  background:
    rgba(73, 127, 231, .09);
}


.stat-icon-active {
  color: #279863;

  background:
    rgba(39, 152, 99, .09);
}


.stat-icon-pending {
  color: #b68024;

  background:
    rgba(206, 151, 48, .10);
}


.stat-icon-archive {
  color: #8268bb;

  background:
    rgba(130, 104, 187, .09);
}


.stat-content {
  min-width: 0;

  display: flex;

  flex-direction: column;

  gap: 1px;
}


.stat-label {
  color:
    #8b929d;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;
}


.stat-content strong {
  color:
    #292e37;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 19px;

  line-height: 1.2;
}


/* ============================================================
   COMMON SECTION
============================================================ */

.vacancies-section {
  position: relative;

  margin-top: 22px;

  padding: 25px;

  background:
    #ffffff;

  border:
    1px solid
    rgba(30, 50, 80, .065);

  border-radius: 24px;

  box-shadow:
    0
    11px
    30px
    rgba(20, 42, 77, .04);

  transition:
    background-color .22s ease,
    border-color .22s ease,
    box-shadow .22s ease;
}


.vacancies-section.drag-over {
  background:
    rgba(52, 127, 231, .035);

  border-color:
    rgba(52, 127, 231, .28);

  box-shadow:
    0
    0
    0
    4px
    rgba(52, 127, 231, .06);
}


.section-header,
.archive-header {
  width: 100%;

  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 20px;
}


.section-title-wrapper {
  min-width: 0;

  display: flex;

  align-items: center;

  gap: 12px;
}


.section-icon {
  width: 42px;
  height: 42px;

  flex:
    0
    0
    42px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 13px;
}


.section-icon svg {
  width: 22px;
  height: 22px;
}


.active-section-icon {
  color: #279863;

  background:
    rgba(39, 152, 99, .085);
}


.pending-section-icon {
  color: #b37b1e;

  background:
    rgba(207, 150, 42, .09);
}


.archive-section-icon {
  color: #8066b8;

  background:
    rgba(128, 102, 184, .09);
}


.section-title-line {
  display: flex;

  align-items: center;

  gap: 7px;
}


.section-title-line h2 {
  margin: 0;

  color: #292e36;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 16px;

  line-height: 1.25;
}


.section-counter {
  min-width: 23px;

  height: 23px;

  display: inline-flex;

  align-items: center;

  justify-content: center;

  padding:
    0
    7px;

  color: #258a5c;

  background:
    rgba(39, 152, 99, .085);

  border-radius: 7px;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 9px;
}


.pending-counter {
  color: #a66f16;

  background:
    rgba(202, 145, 39, .10);
}


.archive-counter {
  color: #765bad;

  background:
    rgba(128, 102, 184, .09);
}


.section-title-wrapper p {
  margin:
    3px
    0
    0;

  color: #8a919c;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;

  line-height: 1.4;
}


/* ============================================================
   PENDING
============================================================ */

.pending-section {
  background:
    linear-gradient(
      135deg,
      rgba(201, 145, 38, .022),
      transparent
    ),
    #ffffff;
}


.pending-info {
  flex:
    0
    0
    auto;

  display: inline-flex;

  align-items: center;

  gap: 6px;

  min-height: 30px;

  padding:
    0
    11px;

  color: #956619;

  background:
    rgba(205, 148, 43, .075);

  border:
    1px solid
    rgba(205, 148, 43, .11);

  border-radius:
    999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
}


.pending-info-dot {
  width: 6px;
  height: 6px;

  background: #d89b2f;

  border-radius: 50%;

  box-shadow:
    0
    0
    0
    4px
    rgba(216, 155, 47, .10);
}


.pending-card-wrapper {
  opacity: .94;
}


.pending-status {
  color: #986717;

  background: #fff8e9;

  border-color:
    rgba(207, 149, 39, .15);

  box-shadow:
    0
    5px
    14px
    rgba(177, 118, 21, .07);
}


.pending-status-icon {
  width: 14px;
  height: 14px;
}


.pending-status-icon svg {
  width: 14px;
  height: 14px;
}


.pending-empty {
  min-height: 110px;

  display: flex;

  align-items: center;

  gap: 12px;

  margin-top: 20px;

  padding:
    19px
    21px;

  background:
    rgba(205, 148, 43, .035);

  border:
    1px dashed
    rgba(205, 148, 43, .16);

  border-radius: 17px;
}


.pending-empty-icon {
  width: 42px;
  height: 42px;

  flex:
    0
    0
    42px;

  display: flex;

  align-items: center;

  justify-content: center;

  color: #b98225;

  background:
    rgba(205, 148, 43, .08);

  border-radius: 13px;
}


.pending-empty-icon svg {
  width: 22px;
  height: 22px;
}


.pending-empty > div:last-child {
  display: flex;

  flex-direction: column;

  gap: 3px;
}


.pending-empty strong {
  color: #515762;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 11px;
}


.pending-empty span {
  color: #969ca5;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;
}


/* ============================================================
   CARDS
============================================================ */

.vacancies-list {
  display: grid;

  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    );

  gap: 19px;

  margin-top: 21px;
}


.vacancy-card-wrapper {
  position: relative;

  min-width: 0;

  padding-top: 15px;
}


.vacancy-card-status {
  position: absolute;

  top: 0;
  left: 15px;

  z-index: 5;

  min-height: 27px;

  display: inline-flex;

  align-items: center;

  gap: 6px;

  padding:
    0
    9px;

  color: #278b5d;

  background: #eff9f4;

  border:
    1px solid
    rgba(39, 152, 99, .12);

  border-radius: 999px;

  box-shadow:
    0
    5px
    14px
    rgba(32, 121, 77, .07);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
}


.status-dot {
  width: 6px;
  height: 6px;

  background: #34b575;

  border-radius: 50%;

  box-shadow:
    0
    0
    0
    4px
    rgba(52, 181, 117, .10);
}


/* ============================================================
   ARCHIVE
============================================================ */

.archive-section {
  margin-bottom: 22px;

  background:
    linear-gradient(
      135deg,
      rgba(128, 102, 184, .023),
      transparent
    ),
    #ffffff;
}


.archive-header {
  padding: 0;

  color: inherit;

  background: transparent;

  border: none;

  text-align: left;

  cursor: pointer;
}


.archive-toggle {
  display: inline-flex;

  align-items: center;

  gap: 5px;

  flex-shrink: 0;

  padding:
    8px
    10px;

  color: #777f8b;

  background: #f3f4f6;

  border-radius: 10px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
}


.archive-toggle svg {
  width: 17px;
  height: 17px;

  transition:
    transform .22s ease;
}


.archive-section.expanded
.archive-toggle svg {
  transform:
    rotate(180deg);
}


.archive-content {
  overflow: hidden;
}


.archive-status {
  color: #7865a4;

  background: #f6f2fd;

  border-color:
    rgba(123, 104, 169, .12);
}


.archive-status-icon {
  width: 14px;
  height: 14px;
}


.archive-status-icon svg {
  width: 14px;
  height: 14px;
}


.frozen-card-wrapper {
  opacity: .78;

  transition:
    opacity .18s ease;
}


.frozen-card-wrapper:hover {
  opacity: 1;
}


/* ============================================================
   SECTION EMPTY
============================================================ */

.section-empty,
.archive-empty {
  min-height: 145px;

  display: flex;

  align-items: center;

  gap: 14px;

  margin-top: 20px;

  padding:
    22px
    23px;

  background:
    linear-gradient(
      135deg,
      rgba(52, 127, 231, .04),
      rgba(94, 85, 225, .02)
    );

  border:
    1px dashed
    rgba(52, 110, 205, .18);

  border-radius: 18px;
}


.section-empty-icon {
  width: 47px;
  height: 47px;

  flex:
    0
    0
    47px;

  display: flex;

  align-items: center;

  justify-content: center;

  color: #5d8fde;

  background:
    rgba(52, 127, 231, .07);

  border-radius: 14px;
}


.section-empty-icon svg {
  width: 24px;
  height: 24px;
}


.section-empty-copy,
.archive-empty-copy {
  min-width: 0;

  flex: 1;

  display: flex;

  flex-direction: column;

  gap: 4px;
}


.section-empty-copy strong,
.archive-empty-copy strong {
  color: #3f454f;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 12px;
}


.section-empty-copy span,
.archive-empty-copy span {
  max-width: 500px;

  color: #8d939d;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;

  line-height: 1.5;
}


.section-empty-button {
  flex-shrink: 0;

  min-height: 38px;

  padding:
    0
    14px;

  color: #397dd6;

  background:
    rgba(52, 127, 231, .075);

  border:
    1px solid
    rgba(52, 127, 231, .11);

  border-radius: 11px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  cursor: pointer;
}


.archive-empty {
  justify-content: center;
}


.archive-empty-image {
  width: 58px;
  height: 58px;

  flex-shrink: 0;

  object-fit: contain;

  opacity: .82;
}


/* ============================================================
   DROP
============================================================ */

.drop-zone-message {
  display: flex;

  align-items: center;

  gap: 11px;

  margin-top: 18px;

  padding:
    14px
    16px;

  color: #3977cb;

  background:
    rgba(52, 127, 231, .065);

  border:
    1px dashed
    rgba(52, 127, 231, .27);

  border-radius: 14px;
}


.archive-drop-message {
  color: #7861ad;

  background:
    rgba(123, 99, 178, .065);

  border-color:
    rgba(123, 99, 178, .23);
}


.drop-zone-icon {
  width: 36px;
  height: 36px;

  flex:
    0
    0
    36px;

  display: flex;

  align-items: center;

  justify-content: center;

  background:
    rgba(255, 255, 255, .68);

  border-radius: 11px;
}


.drop-zone-icon svg {
  width: 20px;
  height: 20px;
}


.drop-zone-message > div:last-child {
  min-width: 0;

  display: flex;

  flex-direction: column;

  gap: 2px;
}


.drop-zone-message strong {
  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 10px;
}


.drop-zone-message span {
  opacity: .78;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;
}


/* ============================================================
   FULL EMPTY STATE
   СОХРАНЕНА ИЗ ТВОЕЙ ВЕРСИИ
============================================================ */

.vacancies-empty {
  min-height:
    calc(100dvh - 190px);

  display: flex;

  align-items: center;

  justify-content: center;

  flex-direction: column;

  padding:
    50px
    20px;

  text-align: center;
}


.empty-background {
  position: relative;

  width: 230px;
  height: 200px;

  display: flex;

  align-items: center;

  justify-content: center;
}


.empty-orbit {
  position: absolute;

  border:
    1px solid
    rgba(40, 135, 247, .11);

  border-radius: 50%;

  animation:
    orbit-pulse
    3s
    ease-in-out
    infinite;
}


.orbit-one {
  width: 145px;
  height: 145px;
}


.orbit-two {
  width: 195px;
  height: 195px;

  animation-delay: .45s;
}


.empty-image-wrapper {
  position: relative;

  z-index: 2;

  width: 126px;
  height: 126px;

  display: flex;

  align-items: center;

  justify-content: center;

  background:
    linear-gradient(
      145deg,
      rgba(40, 135, 247, .1),
      rgba(103, 88, 232, .07)
    );

  border:
    1px solid
    rgba(40, 135, 247, .12);

  border-radius: 36px;

  box-shadow:
    0
    20px
    45px
    rgba(33, 82, 150, .12);

  animation:
    empty-float
    3s
    ease-in-out
    infinite;
}


.empty-image {
  width: 88px;
  height: 88px;

  object-fit: contain;
}


.empty-status {
  position: absolute;

  right: -35px;
  bottom: 8px;

  min-height: 30px;

  display: flex;

  align-items: center;

  gap: 7px;

  padding:
    0
    11px;

  color: #247c54;

  background:
    rgba(255, 255, 255, .93);

  border:
    1px solid
    rgba(39, 152, 99, .12);

  border-radius: 999px;

  box-shadow:
    0
    7px
    18px
    rgba(32, 102, 67, .1);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  white-space: nowrap;
}


.empty-status-dot {
  width: 7px;
  height: 7px;

  background: #34b575;

  border-radius: 50%;
}


.empty-label {
  margin-top: 9px;

  color: #6f8db8;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  letter-spacing: .08em;

  text-transform: uppercase;
}


.vacancies-empty
.empty-title {
  margin:
    7px
    0
    0;

  color: #252a32;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 25px;

  line-height: 1.25;

  letter-spacing: -.5px;
}


.empty-subtext {
  max-width: 480px;

  margin:
    10px
    0
    0;

  color: #7b828d;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 13px;

  line-height: 1.6;
}


.primary-action-button {
  min-width: 220px;

  margin-top: 24px;
}


.empty-benefits {
  display: flex;

  align-items: center;

  justify-content: center;

  flex-wrap: wrap;

  gap: 9px;

  margin-top: 20px;
}


.empty-benefits span {
  display: inline-flex;

  align-items: center;

  gap: 5px;

  padding:
    7px
    10px;

  color: #748095;

  background:
    rgba(255, 255, 255, .65);

  border:
    1px solid
    rgba(30, 60, 110, .07);

  border-radius: 999px;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;
}


.empty-benefits svg {
  width: 13px;
  height: 13px;

  color: #309866;
}


/* ============================================================
   GLOBAL OVERLAY
============================================================ */

.global-overlay {
  position: fixed;

  inset: 0;

  z-index: 3000;

  display: flex;

  align-items: center;

  justify-content: center;

  padding: 20px;

  background:
    rgba(10, 13, 20, .58);

  backdrop-filter:
    blur(8px);

  -webkit-backdrop-filter:
    blur(8px);
}


.global-loader-card {
  min-width: 190px;

  display: flex;

  align-items: center;

  justify-content: center;

  flex-direction: column;

  gap: 13px;

  padding:
    24px
    28px;

  color: #ffffff;

  background:
    rgba(38, 40, 46, .88);

  border:
    1px solid
    rgba(255, 255, 255, .1);

  border-radius: 22px;

  box-shadow:
    0
    24px
    60px
    rgba(0, 0, 0, .32);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 11px;
}


.global-spinner {
  width: 36px;
  height: 36px;

  border:
    3px solid
    rgba(255, 255, 255, .24);

  border-top-color:
    #ffffff;

  border-radius: 50%;

  animation:
    spin
    .75s
    linear
    infinite;
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.my-vacancies-container {
  background:
    var(--background-color);
}


html[data-theme="dark"]
.hero-skeleton,
html[data-theme="dark"]
.vacancies-section {
  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(255, 255, 255, .065);

  box-shadow:
    0
    16px
    40px
    rgba(0, 0, 0, .18);
}


html[data-theme="dark"]
.vacancies-hero {
  background:
    radial-gradient(
      circle at 8% 0%,
      rgba(52, 127, 231, .14),
      transparent 32%
    ),
    radial-gradient(
      circle at 95% 100%,
      rgba(105, 91, 226, .10),
      transparent 34%
    ),
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(105, 150, 220, .12);

  box-shadow:
    0
    18px
    48px
    rgba(0, 0, 0, .20);
}


html[data-theme="dark"]
.hero-copy h1,
html[data-theme="dark"]
.section-title-line h2,
html[data-theme="dark"]
.stat-content strong,
html[data-theme="dark"]
.vacancies-empty
.empty-title,
html[data-theme="dark"]
.section-empty-copy strong,
html[data-theme="dark"]
.archive-empty-copy strong {
  color:
    #f0f1f3;
}


html[data-theme="dark"]
.hero-eyebrow {
  color:
    #8495ad;
}


html[data-theme="dark"]
.hero-copy p,
html[data-theme="dark"]
.section-title-wrapper p,
html[data-theme="dark"]
.empty-subtext,
html[data-theme="dark"]
.section-empty-copy span,
html[data-theme="dark"]
.archive-empty-copy span {
  color:
    #8d939d;
}


html[data-theme="dark"]
.stat-card {
  background:
    rgba(255, 255, 255, .035);

  border-color:
    rgba(255, 255, 255, .06);

  box-shadow:
    none;
}


html[data-theme="dark"]
.stat-label {
  color:
    #7f858f;
}


html[data-theme="dark"]
.pending-section {
  background:
    radial-gradient(
      circle at 0% 0%,
      rgba(198, 143, 40, .045),
      transparent 30%
    ),
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );
}


html[data-theme="dark"]
.pending-info {
  color:
    #d4a451;

  background:
    rgba(204, 149, 49, .07);

  border-color:
    rgba(204, 149, 49, .11);
}


html[data-theme="dark"]
.pending-empty {
  background:
    rgba(204, 149, 49, .04);

  border-color:
    rgba(204, 149, 49, .13);
}


html[data-theme="dark"]
.pending-empty strong {
  color:
    #cdd0d5;
}


html[data-theme="dark"]
.pending-empty span {
  color:
    #777d86;
}


html[data-theme="dark"]
.pending-status {
  color:
    #d3a551;

  background:
    #2a2720;

  border-color:
    rgba(207, 151, 48, .14);
}


html[data-theme="dark"]
.archive-section {
  background:
    radial-gradient(
      circle at 0% 0%,
      rgba(128, 102, 184, .05),
      transparent 30%
    ),
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );
}


html[data-theme="dark"]
.archive-toggle {
  color:
    #b3b6bd;

  background:
    rgba(255, 255, 255, .045);
}


html[data-theme="dark"]
.section-empty,
html[data-theme="dark"]
.archive-empty {
  background:
    rgba(255, 255, 255, .025);

  border-color:
    rgba(110, 150, 210, .14);
}


html[data-theme="dark"]
.drop-zone-icon {
  background:
    rgba(255, 255, 255, .055);
}


html[data-theme="dark"]
.empty-image-wrapper {
  background:
    linear-gradient(
      145deg,
      rgba(40, 135, 247, .14),
      rgba(103, 88, 232, .10)
    );

  border-color:
    rgba(70, 140, 240, .16);

  box-shadow:
    0
    20px
    45px
    rgba(0, 0, 0, .20);
}


html[data-theme="dark"]
.empty-status {
  color:
    #86c6a2;

  background:
    rgba(40, 41, 45, .94);

  border-color:
    rgba(58, 167, 107, .16);
}


html[data-theme="dark"]
.empty-benefits span {
  color:
    #a1a7b0;

  background:
    rgba(255, 255, 255, .045);

  border-color:
    rgba(255, 255, 255, .06);
}


html[data-theme="dark"]
.skeleton-line,
html[data-theme="dark"]
.skeleton-stat {
  background:
    #292b30;
}


/* ============================================================
   TRANSITIONS
============================================================ */

.vacancy-list-enter-active,
.vacancy-list-leave-active {
  transition:
    opacity .25s ease,
    transform .25s ease;
}


.vacancy-list-enter-from,
.vacancy-list-leave-to {
  opacity: 0;

  transform:
    translateY(7px)
    scale(.988);
}


.vacancy-list-move {
  transition:
    transform
    .32s
    cubic-bezier(.22, 1, .36, 1);
}


.archive-content-enter-active,
.archive-content-leave-active {
  transition:
    opacity .25s ease,
    transform .25s ease;
}


.archive-content-enter-from,
.archive-content-leave-to {
  opacity: 0;

  transform:
    translateY(-7px);
}


.drop-message-enter-active,
.drop-message-leave-active,
.overlay-enter-active,
.overlay-leave-active {
  transition:
    opacity .18s ease,
    transform .18s ease;
}


.drop-message-enter-from,
.drop-message-leave-to {
  opacity: 0;

  transform:
    translateY(-4px);
}


.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}


/* ============================================================
   ANIMATIONS
============================================================ */

@keyframes spin {

  to {
    transform:
      rotate(360deg);
  }

}


@keyframes action-gradient {

  from {
    background-position:
      0
      50%;
  }

  to {
    background-position:
      230%
      50%;
  }

}


@keyframes content-appear {

  from {
    opacity: 0;

    transform:
      translateY(7px);
  }

  to {
    opacity: 1;

    transform:
      translateY(0);
  }

}


@keyframes skeleton-shimmer {

  to {
    transform:
      translateX(100%);
  }

}


@keyframes empty-float {

  0%,
  100% {
    transform:
      translateY(0);
  }

  50% {
    transform:
      translateY(-7px);
  }

}


@keyframes orbit-pulse {

  0%,
  100% {
    opacity: .45;

    transform:
      scale(.96);
  }

  50% {
    opacity: 1;

    transform:
      scale(1.04);
  }

}


/* ============================================================
   TABLET
============================================================ */

@media (max-width: 900px) {

  .my-vacancies-page {
    width:
      min(
        calc(100% - 30px),
        760px
      );
  }


  .vacancies-list,
  .vacancies-loading {
    grid-template-columns:
      1fr;
  }


  .hero-top {
    flex-direction:
      column;
  }


  .create-vacancy-button {
    width:
      max-content;
  }


  .hero-statistics {
    grid-template-columns:
      repeat(
        2,
        minmax(0, 1fr)
      );
  }

}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 640px) {

  .my-vacancies-page {
    width:
      100%;

    padding:
      20px
      14px
      40px;
  }


  .vacancies-hero {
    padding:
      21px
      17px;

    border-radius:
      22px;
  }


  .hero-main {
    gap:
      11px;
  }


  .hero-icon {
    width:
      45px;

    height:
      45px;

    flex-basis:
      45px;

    border-radius:
      14px;
  }


  .hero-icon svg {
    width:
      24px;

    height:
      24px;
  }


  .hero-copy h1 {
    font-size:
      21px;
  }


  .hero-copy p {
    font-size:
      10px;

    line-height:
      1.55;
  }


  .create-vacancy-button {
    width:
      100%;

    min-height:
      48px;
  }


  .hero-statistics {
    grid-template-columns:
      repeat(
        2,
        minmax(0, 1fr)
      );

    gap:
      8px;

    margin-top:
      22px;
  }


  .stat-card {
    padding:
      11px;
  }


  .stat-icon {
    width:
      34px;

    height:
      34px;

    flex-basis:
      34px;
  }


  .stat-content strong {
    font-size:
      17px;
  }


  .vacancies-section {
    margin-top:
      17px;

    padding:
      18px
      15px;

    border-radius:
      21px;
  }


  .section-title-wrapper {
    align-items:
      flex-start;

    gap:
      10px;
  }


  .section-icon {
    width:
      38px;

    height:
      38px;

    flex-basis:
      38px;
  }


  .section-icon svg {
    width:
      20px;

    height:
      20px;
  }


  .section-title-line h2 {
    font-size:
      14px;
  }


  .section-title-wrapper p {
    font-size:
      9px;
  }


  .pending-info {
    display:
      none;
  }


  .archive-toggle {
    padding:
      7px;

    font-size:
      0;
  }


  .archive-toggle svg {
    width:
      19px;

    height:
      19px;
  }


  .vacancies-list {
    grid-template-columns:
      1fr;

    gap:
      13px;

    margin-top:
      17px;
  }


  .section-empty,
  .archive-empty,
  .pending-empty {
    min-height:
      150px;

    align-items:
      center;

    flex-direction:
      column;

    padding:
      22px
      17px;

    text-align:
      center;
  }


  .section-empty-copy,
  .archive-empty-copy {
    align-items:
      center;
  }


  .section-empty-button {
    width:
      100%;

    min-height:
      41px;
  }


  .drop-zone-message {
    align-items:
      flex-start;

    padding:
      12px
      13px;
  }


  /*
   * ORIGINAL BEAUTIFUL EMPTY
   */

  .vacancies-empty {
    min-height:
      calc(100dvh - 175px);

    padding:
      20px
      16px
      80px;
  }


  .empty-background {
    width:
      200px;

    height:
      175px;
  }


  .empty-image-wrapper {
    width:
      112px;

    height:
      112px;

    border-radius:
      31px;
  }


  .empty-image {
    width:
      77px;

    height:
      77px;
  }


  .empty-status {
    right:
      -23px;

    bottom:
      6px;
  }


  .vacancies-empty
  .empty-title {
    font-size:
      22px;
  }


  .empty-subtext {
    font-size:
      12px;
  }


  .primary-action-button {
    width:
      100%;

    max-width:
      310px;
  }


  .empty-benefits {
    align-items:
      stretch;

    flex-direction:
      column;
  }


  .empty-benefits span {
    justify-content:
      center;
  }


  .skeleton-stats {
    grid-template-columns:
      repeat(
        2,
        1fr
      );

    margin-top:
      50px;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .vacancies-hero,
  .create-vacancy-button,
  .primary-action-button,
  .empty-image-wrapper,
  .empty-orbit,
  .button-loader,
  .global-spinner,
  .skeleton-line::after,
  .skeleton-stat::after {
    animation:
      none;
  }


  .vacancies-section,
  .archive-toggle svg,
  .vacancy-list-enter-active,
  .vacancy-list-leave-active,
  .vacancy-list-move,
  .archive-content-enter-active,
  .archive-content-leave-active,
  .drop-message-enter-active,
  .drop-message-leave-active,
  .overlay-enter-active,
  .overlay-leave-active {
    transition:
      none;
  }

}

.delete-modal-overlay {
  position: fixed;
  inset: 0;

  z-index: 4000;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 20px;

  background:
    rgba(13, 17, 24, .48);

  backdrop-filter:
    blur(7px);

  -webkit-backdrop-filter:
    blur(7px);
}


.delete-modal {
  width:
    min(
      100%,
      410px
    );

  box-sizing:
    border-box;

  padding:
    26px;

  background:
    rgba(255, 255, 255, .98);

  border:
    1px solid
    rgba(35, 45, 60, .07);

  border-radius:
    24px;

  box-shadow:
    0
    28px
    70px
    rgba(20, 30, 50, .22);
}


.delete-modal-icon {
  width:
    48px;

  height:
    48px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    #c25b64;

  background:
    rgba(209, 74, 85, .08);

  border:
    1px solid
    rgba(209, 74, 85, .09);

  border-radius:
    15px;
}


.delete-modal-icon svg {
  width:
    24px;

  height:
    24px;
}


.delete-modal-copy {
  margin-top:
    19px;
}


.delete-modal-label {
  color:
    #a66b70;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    9px;

  letter-spacing:
    .07em;

  text-transform:
    uppercase;
}


.delete-modal-copy h3 {
  margin:
    5px
    0
    0;

  color:
    #333840;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size:
    19px;

  line-height:
    1.3;

  letter-spacing:
    -.3px;
}


.delete-modal-copy p {
  margin:
    10px
    0
    0;

  color:
    #858b95;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size:
    11px;

  line-height:
    1.55;
}


.delete-modal-copy strong {
  color:
    #5f6670;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-weight:
    normal;
}


.delete-modal-actions {
  display:
    grid;

  grid-template-columns:
    1fr
    1fr;

  gap:
    10px;

  margin-top:
    24px;
}


.delete-modal-cancel,
.delete-modal-confirm {
  height:
    43px;

  border:
    none;

  border-radius:
    12px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size:
    11px;

  cursor:
    pointer;

  transition:
    background-color .18s ease,
    transform .18s ease,
    box-shadow .18s ease,
    opacity .18s ease;
}


.delete-modal-cancel {
  color:
    #606771;

  background:
    #f0f1f3;
}


.delete-modal-cancel:hover:not(:disabled) {
  background:
    #e7e9ec;
}


.delete-modal-confirm {
  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  color:
    #ffffff;

  background:
    linear-gradient(
      135deg,
      #d55a64,
      #bc444f
    );

  box-shadow:
    0
    8px
    18px
    rgba(194, 75, 84, .20);
}


.delete-modal-confirm:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0
    11px
    22px
    rgba(194, 75, 84, .27);
}


.delete-modal-cancel:disabled,
.delete-modal-confirm:disabled {
  cursor:
    not-allowed;

  opacity:
    .55;
}


.delete-button-spinner {
  width:
    16px;

  height:
    16px;

  box-sizing:
    border-box;

  border:
    2px solid
    rgba(255, 255, 255, .35);

  border-top-color:
    #ffffff;

  border-radius:
    50%;

  animation:
    spin
    .65s
    linear
    infinite;
}

html[data-theme="dark"]
.delete-modal {
  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(255, 255, 255, .065);

  box-shadow:
    0
    28px
    70px
    rgba(0, 0, 0, .40);
}


html[data-theme="dark"]
.delete-modal-copy h3 {
  color:
    #f0f1f3;
}


html[data-theme="dark"]
.delete-modal-copy p {
  color:
    #858a93;
}


html[data-theme="dark"]
.delete-modal-copy strong {
  color:
    #c3c7ce;
}


html[data-theme="dark"]
.delete-modal-label {
  color:
    #d38c92;
}


html[data-theme="dark"]
.delete-modal-icon {
  color:
    #ed8b93;

  background:
    rgba(218, 84, 95, .09);

  border-color:
    rgba(218, 84, 95, .10);
}


html[data-theme="dark"]
.delete-modal-cancel {
  color:
    #c8cbd1;

  background:
    #2c2f34;
}


html[data-theme="dark"]
.delete-modal-cancel:hover:not(:disabled) {
  background:
    #33363c;
}

.delete-modal-enter-active,
.delete-modal-leave-active {
  transition:
    opacity .22s ease;
}


.delete-modal-enter-active
.delete-modal,
.delete-modal-leave-active
.delete-modal {
  transition:
    opacity .22s ease,
    transform .28s
    cubic-bezier(.22, 1, .36, 1);
}


.delete-modal-enter-from,
.delete-modal-leave-to {
  opacity:
    0;
}


.delete-modal-enter-from
.delete-modal {
  opacity:
    0;

  transform:
    translateY(8px)
    scale(.965);
}


.delete-modal-leave-to
.delete-modal {
  opacity:
    0;

  transform:
    translateY(4px)
    scale(.98);
}

.vacancy-list-enter-active,
.vacancy-list-leave-active {
  transition:
    opacity .25s ease,
    transform .25s ease;
}

.vacancy-list-leave-active {
  position: relative;

  transition:
    opacity .28s ease,
    transform .32s
    cubic-bezier(.4, 0, .2, 1),
    filter .28s ease;
}


.vacancy-list-leave-to {
  opacity:
    0;

  filter:
    blur(3px);

  transform:
    translateX(-18px)
    scale(.96);
}


.vacancy-list-move {
  transition:
    transform .36s
    cubic-bezier(.22, 1, .36, 1);
}

</style>